// Loan request generator — creates borrower profiles with hidden risk

const FIRST_NAMES = [
  'James','Maria','Robert','Linda','Michael','Patricia','David','Jennifer',
  'William','Elizabeth','Chen','Aisha','Raj','Yuki','Omar','Sofia',
  'Dmitri','Fatima','Lucas','Isabella','Kwame','Mei','Andre','Priya',
];
const LAST_NAMES = [
  'Smith','Johnson','Williams','Brown','Davis','Garcia','Rodriguez','Wilson',
  'Martinez','Anderson','Taylor','Thomas','Hernandez','Moore','Clark','Lee',
  'Walker','Hall','Young','King','Wright','Chen','Patel','Kim',
];
const BUSINESS_NAMES = [
  'GreenLeaf Solutions','Apex Manufacturing','CloudBridge Tech','Summit Logistics',
  'Horizon Retail Group','BluePeak Ventures','OceanWave Imports','NovaStar Energy',
  'PineRidge Construction','IronForge Industries','SwiftTrade Corp','MapleLine Foods',
  'SilverCrest Properties','CityPulse Media','TerraVault Mining','FreshRoute Delivery',
];
const STARTUP_NAMES = [
  'NeuraLink AI','BlockHive','QuantumLeap Bio','SynthWave Labs','DeepMesh Networks',
  'CryptoForge','NanoSprint Health','OrbitDAO','FluxGrid Energy','GenePrint Dx',
  'MetaHarvest','PulseChain Finance','HoloScape VR','ZeroCarbon Tech','AIgility',
];

const LOAN_PURPOSES_INDIVIDUAL = [
  'Home renovation','Debt consolidation','Medical expenses','Education',
  'Car purchase','Wedding','Emergency fund','Vacation','Moving costs',
];
const LOAN_PURPOSES_BUSINESS = [
  'Equipment purchase','Expansion','Inventory','Working capital',
  'Real estate','Fleet upgrade','Technology upgrade','Marketing campaign',
];
const LOAN_PURPOSES_STARTUP = [
  'Seed funding','Product development','Market launch','Hiring',
  'R&D investment','Patent filing','Infrastructure','Growth capital',
];

export const BORROWER_TYPES = {
  INDIVIDUAL: 'individual',
  BUSINESS: 'business',
  STARTUP: 'startup',
};

const BORROWER_CONFIG = {
  [BORROWER_TYPES.INDIVIDUAL]: {
    emoji: '👤',
    color: '#00aaff',
    amountRange: [5000, 150000],
    creditRange: [450, 820],
    durationRange: [6, 60],
    baseDefaultRate: 0.04,
    label: 'Individual',
  },
  [BORROWER_TYPES.BUSINESS]: {
    emoji: '🏢',
    color: '#00ff88',
    amountRange: [50000, 2000000],
    creditRange: [500, 850],
    durationRange: [12, 84],
    baseDefaultRate: 0.03,
    label: 'Business',
  },
  [BORROWER_TYPES.STARTUP]: {
    emoji: '🚀',
    color: '#aa55ff',
    amountRange: [100000, 5000000],
    creditRange: [350, 700],
    durationRange: [12, 60],
    baseDefaultRate: 0.12,
    label: 'Startup',
  },
};

function rand(min, max) {
  return Math.random() * (max - min) + min;
}
function randInt(min, max) {
  return Math.floor(rand(min, max + 1));
}
function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

// Generate a credit score with a realistic distribution
function generateCreditScore(min, max) {
  // Weighted toward middle with some outliers
  const mid = (min + max) / 2;
  const spread = (max - min) / 2;
  const score = mid + (Math.random() + Math.random() + Math.random() - 1.5) * spread * 0.7;
  return Math.round(Math.max(min, Math.min(max, score)));
}

// Calculate suggested interest rate based on credit score and type
function suggestRate(creditScore, borrowerType, economyState) {
  const baseRates = {
    [BORROWER_TYPES.INDIVIDUAL]: 0.06,
    [BORROWER_TYPES.BUSINESS]: 0.05,
    [BORROWER_TYPES.STARTUP]: 0.10,
  };

  let base = baseRates[borrowerType];

  // Credit score adjustment: lower score = higher rate
  const creditFactor = (800 - creditScore) / 500;
  base += creditFactor * 0.08;

  // Economy adjustment
  if (economyState === 'recession') base += 0.02;
  if (economyState === 'boom') base -= 0.01;

  return Math.max(0.03, Math.min(0.30, base));
}

// Hidden true risk — what the player doesn't see directly
function calculateTrueRisk(creditScore, borrowerType, amount, income) {
  const config = BORROWER_CONFIG[borrowerType];
  let risk = config.baseDefaultRate;

  // Credit score impact (major factor)
  if (creditScore < 500) risk += 0.15;
  else if (creditScore < 600) risk += 0.08;
  else if (creditScore < 700) risk += 0.03;
  else if (creditScore > 750) risk -= 0.02;

  // Debt-to-income ratio
  const dti = amount / (income * 12);
  if (dti > 5) risk += 0.10;
  else if (dti > 3) risk += 0.05;
  else if (dti > 2) risk += 0.02;

  // Random hidden factor — some borrowers are deceptively good/bad
  const hiddenFactor = (Math.random() - 0.5) * 0.06;
  risk += hiddenFactor;

  return Math.max(0.005, Math.min(0.60, risk));
}

// Generate income based on borrower type
function generateIncome(borrowerType) {
  switch (borrowerType) {
    case BORROWER_TYPES.INDIVIDUAL:
      return randInt(2000, 15000);
    case BORROWER_TYPES.BUSINESS:
      return randInt(20000, 500000);
    case BORROWER_TYPES.STARTUP:
      return randInt(5000, 100000);
    default:
      return randInt(3000, 10000);
  }
}

function generateBorrowerName(type) {
  if (type === BORROWER_TYPES.INDIVIDUAL) {
    return `${pick(FIRST_NAMES)} ${pick(LAST_NAMES)}`;
  }
  if (type === BORROWER_TYPES.BUSINESS) {
    return pick(BUSINESS_NAMES);
  }
  return pick(STARTUP_NAMES);
}

// Generate a batch of loan requests for this month
export function generateLoanRequests(month, economyState, difficulty, bankReputation) {
  // Number of requests depends on economy and reputation
  let baseCount = 5;
  if (economyState === 'boom') baseCount += 2;
  if (economyState === 'recession') baseCount -= 1;
  baseCount += Math.floor(bankReputation / 30);

  const count = Math.max(3, Math.min(10, baseCount + randInt(-1, 2)));

  // Borrower type weights by economy
  const typeWeights = {
    boom: { [BORROWER_TYPES.INDIVIDUAL]: 0.35, [BORROWER_TYPES.BUSINESS]: 0.40, [BORROWER_TYPES.STARTUP]: 0.25 },
    normal: { [BORROWER_TYPES.INDIVIDUAL]: 0.45, [BORROWER_TYPES.BUSINESS]: 0.35, [BORROWER_TYPES.STARTUP]: 0.20 },
    recession: { [BORROWER_TYPES.INDIVIDUAL]: 0.55, [BORROWER_TYPES.BUSINESS]: 0.30, [BORROWER_TYPES.STARTUP]: 0.15 },
  };
  const weights = typeWeights[economyState] || typeWeights.normal;

  const loans = [];
  for (let i = 0; i < count; i++) {
    // Pick borrower type
    const r = Math.random();
    let type;
    if (r < weights[BORROWER_TYPES.INDIVIDUAL]) type = BORROWER_TYPES.INDIVIDUAL;
    else if (r < weights[BORROWER_TYPES.INDIVIDUAL] + weights[BORROWER_TYPES.BUSINESS]) type = BORROWER_TYPES.BUSINESS;
    else type = BORROWER_TYPES.STARTUP;

    const config = BORROWER_CONFIG[type];
    const creditScore = generateCreditScore(config.creditRange[0], config.creditRange[1]);
    const amount = Math.round(rand(config.amountRange[0], config.amountRange[1]) / 1000) * 1000;
    const duration = randInt(config.durationRange[0] / 6, config.durationRange[1] / 6) * 6;
    const income = generateIncome(type);
    const suggestedRate = suggestRate(creditScore, type, economyState);
    const trueRisk = calculateTrueRisk(creditScore, type, amount, income);

    const purposes = type === BORROWER_TYPES.INDIVIDUAL ? LOAN_PURPOSES_INDIVIDUAL
      : type === BORROWER_TYPES.BUSINESS ? LOAN_PURPOSES_BUSINESS
      : LOAN_PURPOSES_STARTUP;

    loans.push({
      id: `loan-${month}-${i}-${Date.now()}`,
      borrowerName: generateBorrowerName(type),
      borrowerType: type,
      borrowerEmoji: config.emoji,
      borrowerColor: config.color,
      borrowerLabel: config.label,
      creditScore,
      amount,
      duration, // months
      monthlyIncome: income,
      purpose: pick(purposes),
      suggestedRate: Math.round(suggestedRate * 1000) / 1000,
      trueRisk, // HIDDEN from player
      debtToIncome: Math.round((amount / (income * 12)) * 100) / 100,
      // Player decisions (to be filled)
      approved: null,
      playerRate: null,
    });
  }

  // Sort by amount descending for visual interest
  return loans.sort((a, b) => b.amount - a.amount);
}

export function getBorrowerConfig(type) {
  return BORROWER_CONFIG[type];
}

export function getCreditScoreLabel(score) {
  if (score >= 750) return { label: 'Excellent', color: '#00ff88' };
  if (score >= 700) return { label: 'Good', color: '#88ff00' };
  if (score >= 650) return { label: 'Fair', color: '#ffcc00' };
  if (score >= 550) return { label: 'Poor', color: '#ff8800' };
  return { label: 'Very Poor', color: '#ff3366' };
}

export function getCreditScoreColor(score) {
  if (score >= 750) return '#00ff88';
  if (score >= 700) return '#88ff00';
  if (score >= 650) return '#ffcc00';
  if (score >= 550) return '#ff8800';
  return '#ff3366';
}
